import { createSelector } from "@reduxjs/toolkit";
import { DataStatus, isFinished, isLoading } from "@/shared/lib";
import { createAppSlice } from "@/shared/model";
import * as DashboardsAPI from "../api/dashboardsAPI";
import { NEW_DASHBOARD_ID } from "../ui/constants";
import type { Dashboard } from "./types";

interface State {
  editingDashboard: {
    data: Dashboard;
    status: 'idle' | 'dirty';
  } | null;
  dashboards: | {
    data: Dashboard[];
    status: DataStatus.finished;
  }
  | {
    data: null;
    status: Exclude<DataStatus, DataStatus.finished>;
  };
}

const initialState: State = {
  editingDashboard: null,
  dashboards: {
    data: null,
    status: DataStatus.idle,
  },
};

export const dashboardsSlice = createAppSlice({
  name: 'dashboards',
  initialState,
  reducers: (create) => ({
    setupDashboards: create.asyncThunk<void, Dashboard[]>(
      () => {
        return DashboardsAPI.getDashboards();
      },
      {
        pending: (state) => {
          state.dashboards.status = DataStatus.loading;
        },
        rejected: (state) => {
          state.dashboards.status = DataStatus.error;
          state.dashboards.data = null;
        },
        fulfilled: (state, action) => {
          state.dashboards.data = action.payload;
          state.dashboards.status = DataStatus.finished;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;

            if (
              isLoading(state.dashboards.dashboards.status) ||
              isFinished(state.dashboards.dashboards.status)
            ) {
              return false;
            }
          },
        },
      },
    ),
    createNewDashboard: create.reducer((state) => {
      state.editingDashboard = {
        data: {
          id: NEW_DASHBOARD_ID,
          name: 'Untitled dashboard',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          widgets: [],
        },
        status: 'idle',
      };
    }),
    editDashboard: create.reducer<Dashboard>((state, action) => {
      state.editingDashboard = {
        data: action.payload,
        status: 'idle',
      };
    }),
    updateEditingDashboard: create.reducer<Partial<Dashboard>>((state, action) => {
      state.editingDashboard!.data = {
        ...state.editingDashboard!.data,
        ...action.payload as Dashboard,
      };
      state.editingDashboard!.status = 'dirty';
    }),
    cancelEditingDashboard: create.reducer((state) => {
      state.editingDashboard = null;
    }),
    reset: create.reducer(() => {
      return { ...initialState };
    }),
  }),
  selectors: {
    selectDashboards: createSelector(
      (state: State) => state.dashboards.data,
      (data) => {
        return data || [];
      },
    ),
    selectIsDashboardsLoaded: (state) => {
      return isFinished(state.dashboards.status);
    },
    selectIsDashboardsLoading: (state) => {
      return isLoading(state.dashboards.status);
    },
    selectDashboardsDataStatus: (state) => {
      return state.dashboards.status;
    },
    selectEditingDashboard: (state) => {
      return state.editingDashboard;
    },
    selectIsEditingDashboard: (state) => {
      return !!state.editingDashboard;
    },
    selectIsEditingDashboadrHasUnsavedChanges: (state) => {
      return state.editingDashboard?.status === 'dirty';
    },
    selectIsEditingDashboardNew: (state) => {
      return state.editingDashboard?.data.id === NEW_DASHBOARD_ID;
    },
  },
});

export const {
  selectDashboards,
  selectDashboardsDataStatus,
  selectIsDashboardsLoaded,
  selectIsDashboardsLoading,
  selectEditingDashboard,
  selectIsEditingDashboard,
  selectIsEditingDashboadrHasUnsavedChanges,
  selectIsEditingDashboardNew,
} = dashboardsSlice.selectors;
export const {
  setupDashboards,
  reset,
  createNewDashboard,
  updateEditingDashboard,
  editDashboard,
  cancelEditingDashboard,
} = dashboardsSlice.actions;